import React, { useState, useRef, useEffect } from 'react';
import { Box, useTheme, IconButton, Typography } from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  MessageCircle,
  AudioWaveform,
  Subtitles,
  Sparkles,
  Upload,
} from 'lucide-mui';
import { useSelector, useDispatch } from 'react-redux';
import { DRAWER_WIDTH } from '../../../../constants/ui';
import { RootState } from '../../../../reducers';
import { trackTourView } from '../../../../middleware/posthogMiddleware';
import ChapterAccordionDemo from './ChapterAccordionDemo';
import AskAIDemo from './AskAIDemo';
import TranscriptDemo from './TranscriptDemo';
import MultiAskAIDemo from './MultiAskAIDemo';
import TemplatesDemo from './TemplatesDemo';
import AudioRecorderDemo from './AudioRecorderDemo';
import ImportUploadDemo from './ImportUploadDemo';

interface TourCard {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType;
  icon: React.ComponentType<any>;
}

const tourCards: TourCard[] = [
  {
    id: 'ask-ai',
    title: 'Ask Questions',
    description: 'Chat about your content and get instant answers',
    component: AskAIDemo,
    icon: MessageCircle,
  },
  {
    id: 'import-upload',
    title: 'Import & Upload Media',
    description: 'Add content from URLs or upload files directly',
    component: ImportUploadDemo,
    icon: Upload,
  },
  {
    id: 'transcript',
    title: 'Live Transcript',
    description: "See every word as it's spoken with precise timing",
    component: TranscriptDemo,
    icon: Subtitles,
  },
  {
    id: 'chapter-accordion',
    title: 'AI Summaries and Chapters',
    description: 'Jump to any part of your video instantly',
    component: ChapterAccordionDemo,
    icon: FileText,
  },
  {
    id: 'templates',
    title: 'Create Documents',
    description: 'Turn your recordings into summaries and reports',
    component: TemplatesDemo,
    icon: FileText,
  },
  {
    id: 'audio-recorder',
    title: 'Audio and Screen Recorder',
    description:
      'Capture high-quality audio and screen recordings with live waveforms',
    component: AudioRecorderDemo,
    icon: AudioWaveform,
  },
  {
    id: 'multi-ask-ai',
    title: 'Multi Recording Ask AI',
    description: 'Find patterns and insights across multiple recordings',
    component: MultiAskAIDemo,
    icon: Sparkles,
  },
  // Future cards will be added here
];

const TourCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  // Get drawer state like mediaActions does
  const drawerOpen = useSelector(
    (state: RootState) => state.dashboard.drawerState === 'open'
  );

  // Track if we're programmatically scrolling to prevent scroll event conflicts
  const isAutoScrollingRef = useRef(false);

  const scrollToCard = (index: number) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cards = container.children;

      if (cards[index]) {
        const targetCard = cards[index] as HTMLElement;

        // Calculate the card's position relative to the container
        const cardLeft = targetCard.offsetLeft;
        const cardWidth = targetCard.offsetWidth;
        const containerWidth = container.clientWidth;

        // Calculate the scroll position to center the card
        const cardCenter = cardLeft + cardWidth / 2;
        const containerCenter = containerWidth / 2;
        const scrollPosition = cardCenter - containerCenter;

        // Set flag to prevent scroll event handler from firing
        isAutoScrollingRef.current = true;

        container.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth',
        });

        // Reset flag after scroll animation completes
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, 500);
      }
    }
  };

  // Handle scroll events to update current index based on scroll position
  const handleScroll = () => {
    if (isAutoScrollingRef.current || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const { clientWidth: containerWidth, scrollLeft } = container;
    const containerCenter = scrollLeft + containerWidth / 2;

    // Find which card is closest to the center
    let closestIndex = 0;
    let closestDistance = Infinity;

    for (let i = 0; i < tourCards.length; i += 1) {
      const cardElement = container.children[i] as HTMLElement;
      if (cardElement) {
        const { offsetLeft: cardLeft, offsetWidth: cardWidth } = cardElement;
        const cardCenter = cardLeft + cardWidth / 2;
        const distance = Math.abs(cardCenter - containerCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      }
    }

    // Only update if the index actually changed
    if (closestIndex !== currentIndex) {
      setCurrentIndex(closestIndex);
    }
  };

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Use throttled scroll handler to improve performance
      let scrollTimeout: number;
      const throttledHandleScroll = () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = window.setTimeout(handleScroll, 50);
      };

      container.addEventListener('scroll', throttledHandleScroll, {
        passive: true,
      });
      return () => {
        container.removeEventListener('scroll', throttledHandleScroll);
        if (scrollTimeout) clearTimeout(scrollTimeout);
      };
    }
    return undefined;
  }, [currentIndex]); // Include currentIndex as dependency

  const nextCard = () => {
    const newIndex = (currentIndex + 1) % tourCards.length;
    setCurrentIndex(newIndex);
    scrollToCard(newIndex);
  };

  const prevCard = () => {
    const newIndex = (currentIndex - 1 + tourCards.length) % tourCards.length;
    setCurrentIndex(newIndex);
    scrollToCard(newIndex);
  };

  const handleCardClick = (index: number) => {
    setCurrentIndex(index);
    scrollToCard(index);
  };

  // Track tour card views
  useEffect(() => {
    const card = tourCards[currentIndex];
    dispatch(
      trackTourView({
        cardId: card.id,
        cardTitle: card.title,
        cardIndex: currentIndex,
        totalCards: tourCards.length,
      })
    );
  }, [currentIndex, dispatch]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: {
          xs: '100vw',
          md: drawerOpen
            ? `calc(100vw - ${DRAWER_WIDTH}px)`
            : 'calc(100vw - 56px)',
        },
        overflow: 'hidden',
        mt: 0,
        mb: 2,
        mx: -2, // Negative margins to break out of parent padding (16px)
      }}
    >
      {/* Navigation Buttons */}
      {currentIndex > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%', // Repositioned since we removed the header
            left: 8, // Reduced from 16
            transform: 'translateY(-50%)',
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={prevCard}
            sx={{
              backgroundColor: theme.palette.background.paper,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              width: 48, // Slightly smaller
              height: 48,
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ChevronLeft />
          </IconButton>
        </Box>
      )}

      {currentIndex < tourCards.length - 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%', // Repositioned since we removed the header
            right: 8, // Reduced from 16
            transform: 'translateY(-50%)',
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={nextCard}
            sx={{
              backgroundColor: theme.palette.background.paper,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              width: 48, // Slightly smaller
              height: 48,
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Horizontal Scrolling Cards - Standardized Heights */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          gap: 2, // Reduced gap
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          px: 4, // Reduced padding
          pt: 3, // Top padding to prevent cutoff of scaled cards
          pb: 2, // Bottom padding for better spacing
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
        }}
      >
        {tourCards.map((card, index) => {
          const Component = card.component;
          const Icon = card.icon;
          const isActive = index === currentIndex;

          return (
            <Box
              key={card.id}
              onClick={() => handleCardClick(index)}
              sx={{
                minWidth: isActive
                  ? {
                      xs: 360, // Reduced difference from 380 to 360
                      sm: 420, // Reduced difference from 450 to 420
                      md: 460, // Reduced difference from 500 to 460
                    }
                  : {
                      xs: 340, // Increased from 320 to reduce difference
                      sm: 400, // Increased from 380 to reduce difference
                      md: 440, // Increased from 420 to reduce difference
                    },
                maxWidth: isActive
                  ? {
                      xs: 360,
                      sm: 420,
                      md: 460,
                    }
                  : {
                      xs: 340,
                      sm: 400,
                      md: 440,
                    },
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // Smoother easing for all cards, longer duration
                transform: isActive ? 'scale(1.0)' : 'scale(0.92)', // Much smaller scale difference (was 0.75)
                transformOrigin: 'center center',
                opacity: isActive ? 1 : 0.85, // Much smaller opacity difference (was 0.6)
                filter: 'none',
                flexShrink: 0,
                ...(isActive && {
                  '&:hover': {
                    opacity: 1,
                    filter: 'none',
                    transition: 'all 0.2s ease',
                  },
                }),
                ...(!isActive && {
                  '&:hover': {
                    opacity: 0.95, // Subtle hover opacity increase
                    transform: 'scale(0.94)', // Subtle hover scale increase
                    transition: 'all 0.2s ease',
                  },
                }),
              }}
            >
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: theme.shape.borderRadius * 1.5,
                  overflow: 'hidden',
                  border: 'none', // No border for any cards
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // Smoother easing, longer duration
                  height: 500,
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: isActive
                    ? '0 4px 16px rgba(0, 0, 0, 0.08)' // More subtle shadow for active card
                    : 'none', // No shadow for inactive cards
                }}
              >
                {isActive ? (
                  // Show full component for selected card
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Component />
                  </Box>
                ) : (
                  // Show only icon and title for unselected cards
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 3, // Increased gap
                      p: 3,
                    }}
                  >
                    <Icon size={64} color={theme.palette.text.secondary} />
                    <Typography
                      variant="h5"
                      sx={{
                        textAlign: 'center',
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '1.25rem',
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        textAlign: 'center',
                        color: 'text.secondary',
                        fontSize: '1rem',
                        lineHeight: 1.4,
                        maxWidth: '85%',
                      }}
                    >
                      {card.description}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Dots Indicator - Fixed Position */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          mt: 2, // Reduced margin for better spacing
          height: 24, // Fixed height for dot container
          alignItems: 'center',
        }}
      >
        {tourCards.map((_, index) => (
          <Box
            key={index}
            onClick={() => handleCardClick(index)}
            sx={{
              width: 14, // Slightly larger dots
              height: 14,
              borderRadius: '50%',
              backgroundColor:
                index === currentIndex ? 'primary.main' : 'action.disabled',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor:
                  index === currentIndex ? 'primary.main' : 'primary.main',
                transform: 'scale(1.3)',
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TourCarousel;
